import { useCallback } from 'react';
import type { StorageApi } from '@/lib/api';

/**
 * Uploads a single file via presigned URL.
 * Returns the CDN URL on success, or throws on failure.
 */
async function uploadSingleFile(
  api: StorageApi,
  file: File,
  key: string,
): Promise<{ key: string; cdnUrl: string | null }> {
  // Browsers return empty string for unrecognized extensions (.bundle, .hash, etc.)
  const contentType = file.type || 'application/octet-stream';

  const { uploadUrl, cdnUrl } = await api.getUploadUrl(key, contentType, file.size);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  return { key, cdnUrl };
}

/** Max concurrent uploads to avoid overwhelming the browser/network */
const UPLOAD_CONCURRENCY = 5;

export interface UploadProgress {
  total: number;
  completed: number;
  failed: number;
  inProgress: boolean;
  retrying: boolean;
}

export interface BulkUploadResult {
  succeeded: number;
  failed: string[];
}

/**
 * Returns a bulk upload function for a storage arche.
 * Uploads files concurrently (up to UPLOAD_CONCURRENCY at a time),
 * continues on per-file errors, retries failures once, and reports progress via callback.
 */
export function useStorageUpload(api: StorageApi) {
  const uploadFiles = useCallback(
    async (
      files: { file: File; key: string }[],
      onProgress?: (progress: UploadProgress) => void,
    ): Promise<BulkUploadResult> => {
      const total = files.length;
      let completed = 0;
      const failedKeys: string[] = [];

      onProgress?.({ total, completed, failed: 0, inProgress: true, retrying: false });

      // First pass: process in concurrent batches
      const failedEntries: { file: File; key: string }[] = [];

      for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
        const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
        const results = await Promise.allSettled(
          batch.map(({ file, key }) => uploadSingleFile(api, file, key)),
        );

        for (let j = 0; j < results.length; j++) {
          if (results[j].status === 'fulfilled') {
            completed++;
          } else {
            failedEntries.push(batch[j]);
          }
        }

        onProgress?.({
          total,
          completed,
          failed: failedEntries.length,
          inProgress: true,
          retrying: false,
        });
      }

      // Second pass: retry all failed files once
      if (failedEntries.length > 0) {
        onProgress?.({
          total,
          completed,
          failed: failedEntries.length,
          inProgress: true,
          retrying: true,
        });

        for (let i = 0; i < failedEntries.length; i += UPLOAD_CONCURRENCY) {
          const batch = failedEntries.slice(i, i + UPLOAD_CONCURRENCY);
          const results = await Promise.allSettled(
            batch.map(({ file, key }) => uploadSingleFile(api, file, key)),
          );

          for (let j = 0; j < results.length; j++) {
            if (results[j].status === 'fulfilled') {
              completed++;
            } else {
              failedKeys.push(batch[j].key);
            }
          }

          onProgress?.({
            total,
            completed,
            failed: failedKeys.length,
            inProgress: true,
            retrying: true,
          });
        }
      }

      onProgress?.({
        total,
        completed,
        failed: failedKeys.length,
        inProgress: false,
        retrying: false,
      });

      return { succeeded: completed, failed: failedKeys };
    },
    [api],
  );

  return { uploadFiles };
}
